import React, { useState, useEffect } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./reactDatepickerOverrides.css"; // <-- Cargamos tus overrides
import { format } from "date-fns";

export interface GenericDateTimeRangeValue {
  start: Date | null;
  end: Date | null;
}

export interface GenericDateTimeRangePickerProps {
  label?: string;
  value: GenericDateTimeRangeValue;
  onChange?: (value: GenericDateTimeRangeValue) => void;
  displayFormat?: string; // => "yyyy/MM/dd hh:mm aa"
  disabled?: boolean;
  className?: string;
}

const GenericDateTimeRangePicker: React.FC<GenericDateTimeRangePickerProps> = ({
  label = "",
  value,
  onChange,
  // Ejemplo: año/mes/día con diagonales + 12h con AM/PM
  displayFormat = "yyyy/MM/dd hh:mm aa",
  disabled = false,
  className = "",
}) => {
  const [startDate, setStartDate] = useState<Date | null>(value.start);
  const [endDate, setEndDate] = useState<Date | null>(value.end);
  const [isOpen, setIsOpen] = useState(false);

  // Actualiza fecha de inicio
  const handleStartChange = (date: Date | null) => {
    setStartDate(date);
    onChange?.({ start: date, end: endDate });
  };

  // Actualiza fecha de fin
  const handleEndChange = (date: Date | null) => {
    setEndDate(date);
    onChange?.({ start: startDate, end: date });
  };

  // Construye el texto del input
  const getDisplayValue = () => {
    if (!startDate && !endDate) return "";
    if (startDate && endDate) {
      return (
        format(startDate, displayFormat) + " - " + format(endDate, displayFormat)
      );
    }
    if (startDate) return format(startDate, displayFormat);
    if (endDate) return format(endDate, displayFormat);
    return "";
  };

  // Cierra el picker al presionar "Escape"
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Abre/cierra el datepicker al hacer click en el input o en el ícono
  const toggleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={`range-picker-container ${className}`}>
      {label && <label className="range-picker-label">{label}</label>}

      <div className="range-picker-relative">
        {/* Input principal */}
        <input
          type="text"
          className={`range-picker-input ${disabled ? "range-picker-disabled" : ""}`}
          onClick={toggleOpen}
          value={getDisplayValue()}
          readOnly
          disabled={disabled}
          placeholder="Select date range..."
        />

        {/* Ícono de calendario en la esquina derecha */}
        <i
          className="fa fa-calendar range-picker-calendar-icon"
          aria-hidden="true"
          onClick={toggleOpen}
        />

        {/* Si está abierto, mostramos los calendarios */}
        {isOpen && !disabled && (
          <div className="range-picker-dropdown">
            {/* Calendario Start */}
            <ReactDatePicker
              selected={startDate}
              onChange={handleStartChange}
              inline
              monthsShown={1}
              showTimeInput
              timeFormat="hh:mm aa"                 // 12h con AM/PM
              dateFormat={displayFormat}            // "yyyy/MM/dd hh:mm aa"
              calendarClassName="my-react-datepicker" // <-- Para aplicar overrides
            />

            {/* Calendario End */}
            <ReactDatePicker
              selected={endDate}
              onChange={handleEndChange}
              inline
              monthsShown={1}
              showTimeInput
              timeFormat="hh:mm aa"
              dateFormat={displayFormat}
              calendarClassName="my-react-datepicker"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericDateTimeRangePicker;
